import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import {
  compactLayout,
  getCompactAssistanceBarColor,
  isSinAsistenciaStatus,
  mergePositions,
  transformScheduleToLayout,
} from "../../../utils/index.ts";
import GridLayout from "react-grid-layout";
import {
  generateGradientBackground,
  getDateByDayOfWeek,
  getBackgroundColor,
  isOverlapping,
  scheduleModule,
  weekDays,
  weeks,
} from "../../../utils/constanst.js";
import { HourRange } from "../../../models/HourRange.ts";
import moment from "moment";
import {
  findByExactDateAndBranch,
} from "../../../services/branchSchedule.service.ts";
import { findUnscheduleChecks } from "../../../services/assistanceCheck.service.ts";
import Loader from "../../Loader.tsx";
import { ScheduleRecord } from "../../../models/ScheduleRecord.ts";
import { ColorScheduleProperties } from "../../../models/ColorSchedule.ts";
import { findConfigurationByKey } from "../../../services/configuration.service.ts";
import { PositionArray } from "../../../models/PositionArray.ts";
import { LayoutItem } from "../../../models/Layout.ts";
import { useWebSocket } from "../../../hooks/useWebSocket.tsx";
import { isScheduleItemCoveringEssentialSlot } from "./config-horario-semanal/scheduleKeyUtils.ts";

const ScheduleCompact = ({
  hours,
  existingsSchedules,
  formik,
  positionsArray,
  setPositionsArray,
  selectedWeek,
  selectedDay = 0,
  turno = 0,
  hoursRange,

  layout,
  deleteItem,
  handleResize,
  handleResizeStop,
  onDrag,
  onDragStop,
  setLastItem,

  compactFilters,
  updateLine,
}) => {
  const [loading, setLoading] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const [firstRender, setFirstRender] = useState(true);

  const [schedules, setSchedules] = useState(layout);
  const [colors, setColors] = useState();
  const [configuration, setConfiguration] = useState();
  const { isConnected, assistancesObserver } = useWebSocket();
  const [transparentItems, setTransparentItems] = useState([]);
  const [pressedItem, setPressedItem] = useState(null);
  const [unscheduledChecks, setUnscheduledChecks] = useState<any[]>([]);
  const coloredSchedulesRef = useRef<LayoutItem[] | null>(null);

  const getSelectedDate = useCallback(() => {
    const weekRange =
      typeof selectedWeek === "string"
        ? selectedWeek
        : weeks[selectedWeek]?.value || "";
    const selectedDate = weekRange
      ? getDateByDayOfWeek(weekRange, weekDays[selectedDay]?.name)
      : null;

    return selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
  }, [selectedWeek, selectedDay]);

  const findPositionRow = useCallback(
    (item) => {
      return positionsArray.findIndex((position) => {
        if (position?.title || position?.isDeleted) return false;
        if (position?.shift !== item?.shift) return false;

        const itemPositionId = Number(item?.positionId);
        const itemOriginalPositionId = Number(item?.positionOriginalId_);
        const positionId = Number(position?.positionId);
        const positionOriginalId = Number(position?.positionOriginalId_);
        const sameId =
          (itemPositionId && itemPositionId === positionId) ||
          (itemOriginalPositionId && itemOriginalPositionId === positionOriginalId);
        const sameName =
          position?.positionName === item?.position ||
          position?.originalPositionName === item?.position;

        return sameId || sameName;
      });
    },
    [positionsArray]
  );

  const alignLayoutRows = useCallback(
    (items = []) =>
      items.map((item) => {
        const positionRow = findPositionRow(item);
        return positionRow >= 0 ? { ...item, y: positionRow } : item;
      }),
    [findPositionRow]
  );

  const usesAssistanceColors =
    compactFilters.realTime ||
    compactFilters.assistence ||
    compactFilters.late ||
    compactFilters.toArrive ||
    compactFilters.withoutAssistence ||
    compactFilters.fueraDeHorario;

  const handleMakeTransparent = (id: string) => {
    setTransparentItems((prev) => [...prev, id]);
  };
  const calculatePercentage = () => {
    const hours = hoursRange;
    const percentageTotal = 100 / hours.length - scheduleModule;

    let currentHour: string | number = new Date().getHours();
    const currentMinute = new Date().getMinutes();

    currentHour = currentHour < 10 ? `0${currentHour}` : currentHour;

    const hourInFormat = `${currentHour}:${currentMinute < 30 ? "00" : "30"}`;

    const currentIndex =
      hours.findIndex((item: HourRange) => {
        if (item.display === "Hora") return false;

        const [start, end] = item.display.split(" - ");

        return hourInFormat >= start && hourInFormat < end;
      }) - 2;

    if (currentIndex < 0) return setPercentage(0);

    const percentage = currentIndex * percentageTotal;

    const percentageMinute = ((currentMinute % 30) / 30) * 3.01912655971;

    setPercentage(percentage + percentageMinute);
  };

  const moveToCurrentTime = () => {
    const element: HTMLElement | null = document.getElementById("lineaActual");
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
    setFirstRender(false);
  };

  const fetchDependencies = useCallback(async () => {
    const selectedDate = getSelectedDate();

    try {
      const configuration = await findConfigurationByKey("scheduleColor");
      setColors(configuration);
      const assistance = await findConfigurationByKey("assistance");
      setConfiguration(assistance);
      const fetchedSchedules = await findByExactDateAndBranch(
        selectedDate,
        Number(formik.values?.branchId)
      );
      const unscheduled = await findUnscheduleChecks(
        Number(formik.values?.branchId),
        selectedDate,
        selectedDate
      );
      // hay de 2 podemos encontrar sus asistencias 1 por una o
      const transformLayout = transformScheduleToLayout(
        fetchedSchedules,
        configuration,
        assistance,
        { defaultExactDate: selectedDate },
      );
      const alignedLayout = alignLayoutRows(transformLayout);

      coloredSchedulesRef.current = alignedLayout;
      if (usesAssistanceColors) {
        setSchedules(alignedLayout);
      }
      setUnscheduledChecks(unscheduled || []);
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setLoading(false);
    }
  }, [formik.values?.branchId, getSelectedDate, alignLayoutRows, usesAssistanceColors]);

  useEffect(() => {
    if (usesAssistanceColors) {
      if (coloredSchedulesRef.current?.length) {
        setSchedules(alignLayoutRows(coloredSchedulesRef.current));
      } else {
        fetchDependencies();
      }
      return;
    }
    if (compactFilters.resumen) {
      setSchedules(alignLayoutRows(layout || []));
    }
  }, [
    usesAssistanceColors,
    compactFilters.resumen,
    layout,
    alignLayoutRows,
    fetchDependencies,
  ]);

  useEffect(() => {
    calculatePercentage();
    const interval = setInterval(() => {
      calculatePercentage();
      if (compactFilters.realTime) {
        fetchDependencies();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [compactFilters.realTime, fetchDependencies]);

  useEffect(() => {
    if (firstRender && percentage > 0) {
      moveToCurrentTime();
    }
    moveToCurrentTime();
  }, [percentage]);

  useEffect(() => {
    coloredSchedulesRef.current = null;
    fetchDependencies();
  }, [fetchDependencies]);

  useEffect(() => {
    moveToCurrentTime();
  }, [compactFilters]);

  useEffect(() => {
    if (isConnected) {
      const unsubscribe = assistancesObserver((data) => {
        const { branchId } = data;

        if (branchId === Number(formik.values?.branchId)) {
          fetchDependencies();
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [isConnected]);

  const createUnscheduleLayout = () => {
    if (!unscheduledChecks.length || !hoursRange.length) return [];

    const layoutItems: any[] = [];
    const merged = mergePositions(positionsArray);
    const headerOffset = 1;
    const baseY = merged.length + headerOffset;
    let rowIndex = 0;

    unscheduledChecks.forEach((unscheduleItem) => {
      if (!unscheduleItem?.records) return;

      if (unscheduleItem.records.records && unscheduleItem.records.partners) {
        const { records, partners } = unscheduleItem.records;

        partners.forEach((partner: any) => {
          if (!partner?.candidate) return;

          const partnerChecks = records.filter(
            (r) => r.partner?.id === partner.id
          );
          if (partnerChecks.length === 0) return;

          const sortedChecks = partnerChecks.sort((a, b) => {
            const timeA = moment(a.checkTime, "HH:mm:ss");
            const timeB = moment(b.checkTime, "HH:mm:ss");
            return timeA.diff(timeB);
          });

          const firstCheck = sortedChecks[0];
          const lastCheck =
            sortedChecks.length > 1 &&
            sortedChecks[sortedChecks.length - 1].id !== firstCheck.id
              ? sortedChecks[sortedChecks.length - 1]
              : null;

          const checkTime = moment(firstCheck.checkTime, "HH:mm:ss");
          const hourStr = checkTime.format("HH:mm");

          let xIndex = hoursRange.findIndex((hour, idx) => {
            if (hour.display === "Hora" || idx < 2) return false;
            const [start, end] = hour.display.split(" - ");
            return hourStr >= start && hourStr < end;
          });

          if (xIndex === -1) {
            let closestIndex = -1;
            let minDiff = Infinity;
            const checkMoment = moment(hourStr, "HH:mm");

            hoursRange.forEach((hour, idx) => {
              if (hour.display === "Hora" || idx < 2) return;
              const [start] = hour.display.split(" - ");
              const startMoment = moment(start, "HH:mm");
              const diff = Math.abs(checkMoment.diff(startMoment, "minutes"));

              if (diff < minDiff) {
                minDiff = diff;
                closestIndex = idx;
              }
            });

            if (closestIndex >= 2) {
              xIndex = closestIndex;
            } else {
              return;
            }
          }

          const finalXIndex = xIndex >= 2 ? xIndex - 2 : 0;
          if (finalXIndex < 0) return;

          const width = Math.min(
            16,
            Math.max(2, hoursRange.length - finalXIndex - 2)
          ); // fijo 8 hrs

          const partnerName = partner.person?.firstName
            ? `${partner.person.firstName} ${partner.person.lastName || ""} ${
                partner.person.secondLastName || ""
              }`.trim()
            : "Sin nombre";

          layoutItems.push({
            i: `unscheduled-${partner.id}-${firstCheck.id}`,
            minH: 1,
            maxH: 1,
            minW: 2,
            show: true,
            pending: false,
            name: `${partnerName} (NO PROGRAMADO)`,
            position:
              partner.candidate?.vacancy?.position?.name || "Sin posición",
            originalId: partner.id,
            partnerId: partner.id,
            shift: partner.candidate?.shift?.name || "Sin turno",
            x: finalXIndex,
            y: baseY + rowIndex,
            w: width,
            h: 1,
            positionId: -1,
            positionOriginalId_: -1,
            assistanceStatus: "Sin horario programado",
            color: "#9CA3AF",
            startTime: hourStr,
            isUnschedule: true,
            entradaTime: hourStr,
            salidaTime: lastCheck
              ? moment(lastCheck.checkTime, "HH:mm:ss").format("HH:mm")
              : null,
          });

          rowIndex++;
        });
      }
    });

    return layoutItems;
  };

  const unscheduleLayout = createUnscheduleLayout();
  const mergedPositions = mergePositions(positionsArray);
  const compactedSchedules = compactLayout(schedules, mergedPositions);
  const finalLayout = [...compactedSchedules, ...unscheduleLayout];
  const headerOffset = unscheduleLayout.length > 0 ? 1 : 0;
  const maxRows =
    mergedPositions?.length + headerOffset + (unscheduleLayout.length > 0 ? unscheduleLayout.length : 0);

  return (
    <div className="d-flex flex-row">
      <Loader isLoading={loading} />
      <div
        style={{
          position: "sticky",
          left: -10,
          zIndex: 6,
          backgroundColor: "white",
        }}
      >
        {mergedPositions
          .filter((position) => !position.isDeleted)
          .map((key, index) => {
            return (
              <div
                key={index}
                data-grid={{
                  x: 0,
                  y: index,
                  w: key?.title ? 36 : 2,
                  h: 1,
                  static: true,
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "left",
                  borderRight: "2px solid #EEE",
                  borderBottom: `${key?.title ? "3px" : "2px"} solid #EEE`,
                  borderTop: `${key?.title ? "3px solid" : ""} ${
                    key?.title === "Matutino"
                      ? "#FFD700"
                      : key.title === "Vespertino"
                      ? "#FF6347"
                      : "#4682B4"
                  }`,
                  paddingInline: "5px",
                  fontWeight: 600,
                  height: 50,
                  borderLeft: `3px solid ${
                    !key?.shifts
                      ? key.shift === "Matutino"
                        ? "#FFD700"
                        : key.shift === "Vespertino"
                        ? "#FF6347"
                        : "#4682B4"
                      : "#4682B4"
                  }`,
                  backgroundColor: "transparent",
                  width: (2400 / hoursRange?.length) * 2,
                }}
              >
                {key.title && (
                  <p className="w-100 mt-3 fs-15px fw-800 bg">
                    {key?.title ?? key.positionName}
                  </p>
                )}
                <Tooltip title={key?.shifts?.join(", ") ?? ""}>
                  <div>
                    <p className="w-100 mt-auto">{key?.positionName}</p>
                  </div>
                </Tooltip>
              </div>
            );
          })}
        {unscheduleLayout.length > 0 && (
          <div
            key="unschedule-header"
            data-grid={{
              x: 0,
              y: mergedPositions.length,
              w: 2,
              h: 1,
              static: true,
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "10px",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              borderRight: "2px solid #EEE",
              borderBottom: "1px solid #EEE",
              borderTop: "2px solid #9CA3AF",
              paddingInline: "6px",
              fontWeight: 700,
              height: 50,
              borderLeft: "3px solid #9CA3AF",
              backgroundColor: "#F3F4F6",
              width: Math.min(
                130,
                hoursRange?.length ? (2400 / hoursRange.length) * 2 : 100
              ),
            }}
          >
            <p
              className="w-100"
              style={{ whiteSpace: "nowrap", marginBottom: 0 }}
            >
              No programado
            </p>
          </div>
        )}
        {unscheduleLayout.length > 0 &&
          unscheduleLayout.map((item, idx) => (
            <div
              key={`unschedule-sidebar-${idx}`}
              data-grid={{
                x: 0,
                y: mergedPositions.length + 1 + idx,
                w: 2,
                h: 1,
                static: true,
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "10px",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                borderRight: "2px solid #EEE",
                borderBottom: "2px solid #EEE",
                paddingInline: "6px",
                fontWeight: 600,
                height: 50,
                borderLeft: "3px solid #9CA3AF",
                backgroundColor: "#F9FAFB",
                width: Math.min(
                  130,
                  hoursRange?.length ? (2400 / hoursRange.length) * 2 : 100
                ),
              }}
            >
              <p
                className="w-100"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginBottom: 0,
                }}
              >
                {item.name}
              </p>
            </div>
          ))}
      </div>

      <div
        style={{
          position: "relative",
          //cursor: "not-allowed",
        }}
      >
        {/* {JSON.stringify(schedules)} */}
        <GridLayout
          // className="layout"
          cols={hoursRange.length}
          layout={finalLayout}
          maxRows={maxRows}
          rowHeight={50}
          width={2400}
          preventCollision={false}
          allowOverlap={true}
          isResizable={false}
          isDroppable={false}
          isDraggable={false}
          draggable={false}
          onResize={handleResize}
          onResizeStop={handleResizeStop}
          onDrag={onDrag}
          onDragStop={onDragStop}
          compactType={null}
          margin={[0, 0]}
          style={{
            background: usesAssistanceColors
              ? generateGradientBackground(hoursRange, hours, 3)
              : generateGradientBackground(hoursRange, hours, turno),
            width: `${2400}px`,
            position: "relative",
          }}
          onDrop={(layoutx, layoutItem, _event) => {
            setLastItem(layoutItem);
          }}
        >
          {mergedPositions
            .filter((position) => !position.isDeleted)
            .map((key, index) => {
              const isEssentialPositionCovered = (position) => {
                if (!position?.positionIsEssencial || position?.title) return false;
                if (!formik.values.schedule || formik.values.schedule.length === 0) return false;

                return formik.values.schedule.some((scheduleItem) =>
                  isScheduleItemCoveringEssentialSlot(scheduleItem, position),
                );
              };

              return (
                <div
                  key={index}
                  data-grid={{
                    x: 0,
                    y: index,
                    w: key?.title ? 36 : 2,
                    h: 1,
                    static: true,
                  }}
                  style={{
                    fontSize: "10px",
                    display: "none",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "left",
                    borderRight: "2px solid #EEE",
                    borderBottom: `${key?.title ? "3px" : "2px"} solid #EEE`,
                    borderTop: `${key?.title ? "3px solid" : ""} ${
                      key?.title === "Matutino"
                        ? "#FFD700"
                        : key.title === "Vespertino"
                        ? "#FF6347"
                        : "#4682B4"
                    }`,
                    paddingInline: "5px",
                    fontWeight: 600,
                    borderLeft: `3px solid ${
                      key.shift === "Matutino"
                        ? "#FFD700"
                        : key.shift === "Vespertino"
                        ? "#FF6347"
                        : "#4682B4"
                    }`,
                    backgroundColor: isEssentialPositionCovered(key) 
                      ? "#28a745" 
                      : key?.positionIsEssencial && !key?.title 
                      ? "#FFF9E6" 
                      : "white",
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  {key.title && (
                    <p className="w-100 mt-3 fs-15px fw-800 bg">
                      {key?.title ?? key.positionName}
                    </p>
                  )}
                  <p 
                    className="w-100 mt-auto"
                    style={{
                      fontWeight: (isEssentialPositionCovered(key) || (key?.positionIsEssencial && !key?.title)) ? "bold" : "normal",
                      color: isEssentialPositionCovered(key) ? "#ffffff" : "inherit"
                    }}
                  >
                    {key?.positionName}
                  </p>
                </div>
              );
            })}
          {unscheduleLayout.length > 0 &&
            unscheduleLayout.map((item, idx) => (
              <div
                key={`unschedule-spacer-${idx}`}
                data-grid={{
                  x: 0,
                  y: mergedPositions.length + 1 + idx,
                  w: 2,
                  h: 1,
                  static: true,
                }}
                style={{
                  fontSize: "10px",
                  display: "none",
                }}
              />
            ))}

          {finalLayout.map((item, index) => {
              if (item?.x === undefined || !item?.show) return <></>;

              if (
                compactFilters.assistence &&
                item.assistanceStatus !== "Asistencia" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.late &&
                item.assistanceStatus !== "Retardo" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.toArrive &&
                item.assistanceStatus !== "Por llegar" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.withoutAssistence &&
                !isSinAsistenciaStatus(item.assistanceStatus) &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.fueraDeHorario &&
                item.assistanceStatus !== "Fuera de horario" &&
                !item.isUnschedule
              ) {
                return <></>;
              }

              const barColor = getCompactAssistanceBarColor(item, colors);

              return (
                <div
                  key={item?.i}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    opacity: pressedItem === item.i ? 0.1 : 1,
                  }}
                  data-grid={{
                    x: item?.x,
                    y: item?.y,
                    w: item?.w,
                    h: 1,
                  }}
                >
                  <div
                    style={{
                      paddingLeft: "8px",
                      width: "100%",
                      borderRadius: "5px",
                      paddingBlock: "4px",
                      ...(compactFilters.resumen && {
                        ...getBackgroundColor(item, index, positionsArray),
                      }),
                      ...(usesAssistanceColors && {
                        backgroundColor: barColor,
                        border: `1px solid ${barColor}80`,
                        borderLeft: `3px solid ${barColor}`,
                      }),
                      ...(item.isUnschedule && {
                        backgroundColor: "#D1D5DB",
                        border: "1px solid #9CA3AF",
                        borderLeft: "3px solid #6B7280",
                        color: "#111827",
                      }),
                    }}
                    className="shadow-sm"
                  >
                    <Tooltip
                      title={
                        item.isUnschedule
                          ? `${item.name} | ${item.position}`
                          : `${item.name} | ${item.position} | ${item.w / 2} hrs`
                      }
                      arrow
                    >
                      <div className="d-flex flex-row align-items-center">
                        <Typography fontWeight={500}>
                          <div>{item.name}</div>
                        </Typography>
                        {item.overlap && (
                          <a
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              setPressedItem(item.i);
                            }}
                            onMouseUp={(e) => {
                              e.stopPropagation();
                              setPressedItem(null);
                            }}
                            onMouseLeave={() => setPressedItem(null)}
                            style={{ zIndex: 2 }}
                          >
                            <i className="ms-2 fa-solid fa-eye"></i>
                          </a>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
        </GridLayout>

        <div
          id="lineaActual"
          style={{
            position: "absolute",
            top: 0,
            left: `${percentage}%`,
            height: "100%",
            borderLeft: "2px solid red",
            zIndex: 2,
          }}
        />
      </div>
    </div>
  );
};

export default ScheduleCompact;
